/**
 * One audit screen: every field in the group with its two answers, then a single judgement
 * covering the whole group. Hierarchy relatives are one decision — "was the body part right?"
 * spans principal, detailed and symptoms — so asking per field would be three ways to say the
 * same thing.
 *
 * The answers are read-only; the auditor only judges them. A group already judged replays that
 * decision behind an explicit Edit, so revisiting cannot overwrite someone's call by accident.
 */

import React from 'react';
import Button from '@amzn/meridian/button';
import RadioButton from '@amzn/meridian/radio-button';
import Textarea from '@amzn/meridian/textarea';
import { useAppSelector } from '../../../store/store';
import { AuditedField, AuditResult } from '../../../store/sessionSlice';
import {
  FieldValue,
  fieldValuesEqual,
  formatFieldValueOneLine,
  getFieldLabel,
} from '../../../utils/fieldUtils';
import FieldDropdown from '../../shared/session/FieldDropdown';
import {
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  TEXT_COLOR_PRIMARY,
  TEXT_COLOR_SECONDARY,
} from '../../shared/cssConstants';

/** How each result reads — used for the radio options and for replaying a recorded decision. */
const RESULT_LABELS: Record<AuditResult, string> = {
  user_correct: "User's answer is correct",
  llm_correct: 'LLM answer is correct',
  both_correct: 'Both answers are correct',
  neither_correct: 'Neither answer is correct',
};

const CONFLICT_OPTIONS = (
  ['user_correct', 'llm_correct', 'both_correct', 'neither_correct'] as AuditResult[]
).map((result) => ({ result, label: RESULT_LABELS[result] }));

/** Shown when user and LLM agree on every field in the group — there is no side to pick. */
const AGREEMENT_OPTIONS: { result: AuditResult; label: string }[] = [
  { result: 'both_correct', label: 'The answer is correct' },
  { result: 'neither_correct', label: 'The answer is not correct' },
];

const AuditGroupEditor: React.FC<{
  fields: string[];
  llmValues: Record<string, FieldValue>;
  userValues: Record<string, FieldValue>;
  /** A hierarchical field's options depend on its parent's value. */
  parentValueFor: (fieldName: string) => FieldValue | undefined;
  answersAgree: boolean;
  result?: AuditResult;
  onResultChange: (result: AuditResult) => void;
  confirmedValues: Record<string, FieldValue>;
  onConfirmedValueChange: (fieldName: string, value: FieldValue) => void;
  comment: string;
  onCommentChange: (comment: string) => void;
  /** The decision already on record for this group, if an auditor has been here before. */
  priorAudit?: AuditedField;
  editing: boolean;
  onEditToggle: () => void;
}> = ({
  fields,
  llmValues,
  userValues,
  parentValueFor,
  answersAgree,
  result,
  onResultChange,
  confirmedValues,
  onConfirmedValueChange,
  comment,
  onCommentChange,
  priorAudit,
  editing,
  onEditToggle,
}) => {
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const options = answersAgree ? AGREEMENT_OPTIONS : CONFLICT_OPTIONS;
  // Either this group is untouched, or the auditor has chosen to override the recorded decision.
  const isDeciding = !priorAudit || editing;

  const answerRow = (title: string, value: FieldValue | undefined) => (
    <div style={{ marginTop: '4px' }}>
      <div style={{ fontSize: FONT_SIZE_SMALL, fontWeight: 600, color: TEXT_COLOR_SECONDARY }}>
        {title}
      </div>
      <div style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY }}>
        {formatFieldValueOneLine(value)}
      </div>
    </div>
  );

  return (
    <div style={{ marginBottom: '24px' }}>
      {fields.map((field) => (
        <div key={field} style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: FONT_SIZE_BODY, fontWeight: 600, color: TEXT_COLOR_PRIMARY }}>
            {getFieldLabel(fieldsConfig, field)}
          </div>
          {fieldValuesEqual(userValues[field], llmValues[field]) ? (
            // Say so explicitly — a lone "Answer" row leaves the auditor guessing whether the
            // two sides matched or one was simply missing.
            answerRow('User and LLM answer (same)', userValues[field])
          ) : (
            <>
              {answerRow("User's answer", userValues[field])}
              {answerRow('LLM answer', llmValues[field])}
            </>
          )}
        </div>
      ))}

      {/* A decision already on record: show whose it was, and require an explicit Edit to
          override it, so revisiting a group cannot change it by a stray click. */}
      {priorAudit && !editing ? (
        <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '12px', marginBottom: '8px' }}>
          <div style={{ fontSize: FONT_SIZE_SMALL, fontWeight: 600, color: TEXT_COLOR_SECONDARY }}>
            Audited
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY }}>
              {RESULT_LABELS[priorAudit.result]}
            </span>
            <Button type="link" size="small" onClick={onEditToggle}>
              Edit
            </Button>
          </div>
          <div style={{ fontSize: FONT_SIZE_SMALL, color: TEXT_COLOR_SECONDARY }}>
            by {priorAudit.audited_by}
            {priorAudit.audited_at && ` · ${new Date(priorAudit.audited_at).toLocaleString()}`}
          </div>
          {priorAudit.result === 'neither_correct' &&
            fields.map((field) => (
              <div key={field} style={{ fontSize: FONT_SIZE_SMALL, color: TEXT_COLOR_SECONDARY, marginTop: '4px' }}>
                Correct {getFieldLabel(fieldsConfig, field)}:{' '}
                {formatFieldValueOneLine(confirmedValues[field])}
              </div>
            ))}
          {priorAudit.comment && (
            <div style={{ fontSize: FONT_SIZE_SMALL, color: TEXT_COLOR_SECONDARY, marginTop: '4px' }}>
              &ldquo;{priorAudit.comment}&rdquo;
            </div>
          )}
        </div>
      ) : (
        /* One judgement for the group */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            borderTop: '1px solid #e0e0e0',
            paddingTop: '12px',
            marginBottom: '8px',
          }}
        >
          {options.map((option) => (
            <RadioButton
              key={option.result}
              checked={result === option.result}
              value={option.result}
              onChange={() => onResultChange(option.result)}
            >
              {option.label}
            </RadioButton>
          ))}
        </div>
      )}

      {isDeciding && result === 'neither_correct' && (
        <div>
          {fields.map((field) => (
            <FieldDropdown
              key={field}
              fieldName={field}
              value={confirmedValues[field]}
              parentValue={parentValueFor(field)}
              label={`Correct ${getFieldLabel(fieldsConfig, field)}`}
              onChange={onConfirmedValueChange}
            />
          ))}
          <div style={{ fontSize: FONT_SIZE_SMALL, color: TEXT_COLOR_SECONDARY, marginTop: '4px' }}>
            Recorded for audit metrics only — this does not change the session.
          </div>
        </div>
      )}

      {isDeciding && result && (
        <div style={{ marginTop: '12px' }}>
          <Textarea
            value={comment}
            onChange={(value: string) => onCommentChange(value)}
            label="Comment (optional)"
            placeholder="Explain your assessment"
            rows={2}
            size="small"
          />
        </div>
      )}
    </div>
  );
};

export default AuditGroupEditor;
